import { Link } from "react-router-dom";
import UniversalLayout from "./components/UniversalLayout";
import {
  BLOG_SITE_DESCRIPTION,
  buildBlogPostPath,
  getSortedBlogPosts,
} from "./content/blogPosts";
import { useTheme } from "./hooks/useTheme";
import "./BlogPage.css";

function formatPublishedDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getCoverImageSource(post, isDarkTheme) {
  if (isDarkTheme && post.coverImageDarkPath) {
    return post.coverImageDarkPath;
  }
  return post.coverImagePath || "/latted_social_preview_dark.png";
}

function FeaturedPostCard({ post, isDarkTheme }) {
  const coverImageSource = getCoverImageSource(post, isDarkTheme);

  return (
    <article className="blog-page__featured">
      <Link to={buildBlogPostPath(post.slug)} className="blog-page__featured-cover-link">
        <img
          src={coverImageSource}
          alt={post.coverImageAlt || post.title}
          className="blog-page__featured-cover"
          loading="eager"
          onError={(event) => {
            event.currentTarget.src = "/latted_social_preview_dark.png";
          }}
        />
      </Link>

      <div className="blog-page__featured-body">
        <div className="blog-page__feature-label-row">
          <div className="blog-page__featured-label">Latest</div>
          {post.contentType ? (
            <span className="blog-page__card-type">{post.contentType}</span>
          ) : null}
        </div>

        <div className="blog-page__meta-row">
          <time dateTime={post.publishedAt} className="blog-page__meta-date">
            {formatPublishedDate(post.publishedAt)}
          </time>
          <span aria-hidden="true">·</span>
          <span>{post.readingTimeMinutes} min read</span>
          <span aria-hidden="true">·</span>
          <span>{post.authorName}</span>
        </div>

        <h2 className="blog-page__featured-title">
          <Link to={buildBlogPostPath(post.slug)}>{post.title}</Link>
        </h2>

        <p className="blog-page__featured-excerpt">{post.description}</p>

        <div className="blog-page__tags" aria-label="Article tags">
          {post.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="blog-page__tag">
              {tag}
            </span>
          ))}
        </div>

        <Link to={buildBlogPostPath(post.slug)} className="blog-page__read-more">
          Read article
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
      </div>
    </article>
  );
}

function BlogPostCard({ post, isDarkTheme }) {
  const coverImageSource = getCoverImageSource(post, isDarkTheme);

  return (
    <article className="blog-page__card">
      <Link to={buildBlogPostPath(post.slug)} className="blog-page__cover-link">
        <img
          src={coverImageSource}
          alt={post.coverImageAlt || post.title}
          className="blog-page__cover"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = "/latted_social_preview_dark.png";
          }}
        />
      </Link>

      <div className="blog-page__card-body">
        <div className="blog-page__feature-label-row">
          <span className="blog-page__card-type">
            {post.contentType || "Article"}
          </span>
        </div>

        <div className="blog-page__meta-row">
          <time dateTime={post.publishedAt} className="blog-page__meta-date">
            {formatPublishedDate(post.publishedAt)}
          </time>
          <span aria-hidden="true">·</span>
          <span>{post.readingTimeMinutes} min read</span>
          <span aria-hidden="true">·</span>
          <span>{post.authorName}</span>
        </div>

        <h2 className="blog-page__card-title">
          <Link to={buildBlogPostPath(post.slug)}>{post.title}</Link>
        </h2>

        <p className="blog-page__card-excerpt">{post.excerpt}</p>

        <div className="blog-page__tags" aria-label="Article tags">
          {post.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="blog-page__tag">
              {tag}
            </span>
          ))}
        </div>

        <Link to={buildBlogPostPath(post.slug)} className="blog-page__read-more">
          Read article
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
      </div>
    </article>
  );
}

function BlogPage() {
  const posts = getSortedBlogPosts();
  const isDarkTheme = useTheme();
  const [featuredPost, ...remainingPosts] = posts;

  return (
    <UniversalLayout>
      <div className="blog-page">
        <header className="blog-page__hero">
          <p className="blog-page__kicker">Latted Blog</p>
          <h1 className="blog-page__title">
            AI video tutorials, creator reviews, and workflow notes.
          </h1>
          <p className="blog-page__subtitle">{BLOG_SITE_DESCRIPTION}</p>
        </header>

        {featuredPost && (
          <section
            id="latest"
            className="blog-page__featured-section"
            aria-labelledby="blog-page-featured-heading"
          >
            <p className="blog-page__section-label">Latest article</p>
            <h2 id="blog-page-featured-heading" className="blog-page__section-title">
              Start here
            </h2>

            <FeaturedPostCard post={featuredPost} isDarkTheme={isDarkTheme} />
          </section>
        )}

        {remainingPosts.length > 0 && (
          <section
            id="articles"
            className="blog-page__articles-section"
            aria-labelledby="blog-page-articles-heading"
          >
            <p className="blog-page__section-label">Recent posts</p>
            <h2 id="blog-page-articles-heading" className="blog-page__section-title">
              All articles
            </h2>

            <div className="blog-page__grid">
              {remainingPosts.map((post) => (
                <BlogPostCard key={post.slug} post={post} isDarkTheme={isDarkTheme} />
              ))}
            </div>
          </section>
        )}
      </div>
    </UniversalLayout>
  );
}

export default BlogPage;

